import { SbkElement } from '../../configuration/sbk-element.ts';
import { customElement, property } from 'lit/decorators.js';
import { style } from './button.styles';
import { html, nothing } from 'lit';
import { FormControlDependencyMixin } from '@/components/form-control-dependency/form-control-dependency-mixin';


/**
 * @slot default - Inhalt / Label
 */
@customElement('sbk-button')
export class SbkButton extends FormControlDependencyMixin(SbkElement) {

    @property({ type: String })
    name? = ''

    @property({ type: String })
    icon? = ''

    @property({ attribute: 'icon-position' })
    iconPosition?: 'left' | 'right' = 'left'

    @property({ type: Boolean, attribute: 'icon-only' })
    iconOnly? = false;

    @property({ type: Boolean })
    disabled? = false;

    @property({ type: Boolean, attribute: 'hidden' })
    hideBtn?: boolean = false;

    @property({ type: Boolean })
    alternativestyling? = false;

    @property({ type: Boolean })
    inTable? = false;

    @property({ type: String })
    href? = '';

    @property()
    size?: 'large' | 'medium' | 'small' | 'adaptive' | 'xsmall' = 'adaptive';

    @property()
    target?: '_blank' | '_parent' | '_self' | '_top' | '' = '';

    @property({ type: String })
    value? = '';

    @property()
    variant: 'cta' | 'meta-cta' | 'primary' | 'secondary' | 'plain' = 'primary';

    @property()
    alternativeStyling?: 'default' | 'alternative';

    @property()
    type: 'button' | 'submit' = 'button';

    @property({ type: Boolean, attribute: 'prevent-disable' })
    preventDisable? = false;

    fakeSubmitButton?: HTMLInputElement;

    connectedCallback() {
        super.connectedCallback();
        if (this.type === 'submit') {
            const fakeSubmitButton = document.createElement('input');
            fakeSubmitButton.type = 'submit';
            fakeSubmitButton.name = this.name ?? '';
            fakeSubmitButton.value = this.value ?? '';
            fakeSubmitButton.hidden = true;
            if (this.preventDisable) {
                fakeSubmitButton.dataset.preventDisable = "1";
            }
            const form = this.closest('form');
            form?.appendChild(fakeSubmitButton);
            this.fakeSubmitButton = fakeSubmitButton;
        }
    }

    static get styles() {
        return style;
    }

    public override _show() {
        this.disabled = false;
    }

    public override _hide() {
        this.disabled = true;
    }

    render() {
        return this.href ? this.renderLink() : this.renderButton();
    }

    private renderButton() {
        return html`
            <button
                part="button"
                title=${this.title || nothing}
                class="button button-${this.variant} button-${this.size} button-${this.alternativeStyling}-styling ${this.inTable ? 'button-table' : ''} ${this.iconOnly ? 'button-icon-only' : ''} ${this.hideBtn ? 'button-hidden' : ''}"
                type='button'
                ?disabled=${this.disabled}
                aria-labelledby="button-label"
                ?alternativestyling=${this.alternativestyling}
                .value=${this.value}
                @click=${this.type === "submit" ? this._handleSubmit : this._handleClick}
            >
                ${this.renderIconLeft()}
                <span id="button-label">
                    <slot></slot>
                </span>
                ${this.renderIconRight()}
            </button>
        `;
    }

    private renderLink() {
        return html`
            <a
              part="button"
              id="button"
              class="button button-${this.variant} ${this.inTable ? 'button-table' : ''} button-${this.size}${this.iconOnly ? ' button-icon-only' : ''}"
              role="button"
              href=${this.href}
              title=${this.title || nothing}
              target=${this.target || nothing}
              ?disabled=${this.disabled}
            >
                ${this.renderIconLeft()}
                <slot class="button__content"></slot>
                ${this.renderIconRight()}
            </a>
        `;
    }

    private renderIconLeft() {
        if (!this.icon && this.variant !== 'cta' || this.icon && this.iconPosition !== 'left') {
            return html`${nothing}`;
        }
        return html`
            <div class="icon-wrapper"><sbk-icon size=${this.getIconSize(this.size)} symbol=${this.variant === 'cta' ? 'logo' : this.icon}></sbk-icon></div>
        `;
    }

    private renderIconRight() {
        if (!this.icon && this.variant !== 'cta' || this.icon && this.iconPosition !== 'right') {
            return html`${nothing}`;
        }
        return html`
            <div class="icon-wrapper"><sbk-icon size=${this.getIconSize(this.size)} symbol=${this.variant === 'cta' ? 'chevron-right' : this.icon}></sbk-icon></div>
        `;
    }

    private getIconSize(size: 'small' | 'medium' | 'large' | 'adaptive' | 'xsmall' | undefined): number {
        switch (size) {
            case 'xsmall':
            case 'small':
            case 'medium':
                return 20;
            case 'large':
                return 24;
            default:
                return 24;
        }
    }

    _handleClick() {
        const clickEvent = new CustomEvent('sbk-click');
        this.dispatchEvent(clickEvent);
    }

    _handleSubmit() {
        const clickEvent = new CustomEvent('sbk-click');
        this.dispatchEvent(clickEvent);
        const form = this.closest('form');
        form?.requestSubmit(this.fakeSubmitButton)
    }
}
